/* ============================================
   MATCHING-KATALOG
   ============================================
   Zentrale, konfigurierbare Quelle für das
   zweiseitige Matching zwischen Pflegekraft und
   Einrichtung. Sowohl beide Funnel-Formulare als
   auch die Matching-Engine lesen ausschließlich hier.

   Wenn eine Frage geändert/ergänzt werden soll,
   passiert das NUR in dieser Datei.
   ============================================ */

/* ---- Kategorien des Arbeitsplatz-Fit (Ebene 2) ---- */

export type CategoryId =
  | 'dienstplan'
  | 'belastung'
  | 'fuehrung'
  | 'pflegequalitaet'
  | 'verguetung'
  | 'setting'
  | 'entwicklung'
  | 'onboarding'

export interface MatchingCategory {
  id: CategoryId
  label: string
  shortLabel: string
  icon: string
  /** Relatives Gewicht innerhalb des Arbeitsplatz-Fit (Ebene 2). */
  weight: number
  candidateIntro: string
  employerIntro: string
}

export const CATEGORIES: MatchingCategory[] = [
  {
    id: 'dienstplan',
    label: 'Dienstplan & Vereinbarkeit',
    shortLabel: 'Dienstplan',
    icon: '📅',
    weight: 14,
    candidateIntro: 'Wie wichtig sind Ihnen verlässliche Dienstpläne und Planbarkeit?',
    employerIntro: 'Bitte schätzen Sie die Dienstplan-Realität in Ihrer Einrichtung ehrlich ein.',
  },
  {
    id: 'belastung',
    label: 'Belastung & Personalbesetzung',
    shortLabel: 'Belastung',
    icon: '⚖️',
    weight: 14,
    candidateIntro: 'Wie wichtig sind Ihnen ein machbares Pensum und gute Besetzung?',
    employerIntro: 'Wie sieht die tatsächliche Arbeitsbelastung in Ihrer Einrichtung aus?',
  },
  {
    id: 'fuehrung',
    label: 'Führung, Team & Anerkennung',
    shortLabel: 'Führung & Team',
    icon: '🤝',
    weight: 11,
    candidateIntro: 'Wie wichtig sind Ihnen Führungskultur, Team und Wertschätzung?',
    employerIntro: 'Wie würden Sie die Führungs- und Teamkultur Ihrer Einrichtung beschreiben?',
  },
  {
    id: 'pflegequalitaet',
    label: 'Pflegequalität & Handlungsspielraum',
    shortLabel: 'Pflegequalität',
    icon: '💚',
    weight: 11,
    candidateIntro: 'Wie wichtig sind Ihnen Qualität und fachlicher Spielraum?',
    employerIntro: 'Wie viel Raum für gute Pflege und Fachlichkeit gibt es realistisch?',
  },
  {
    id: 'setting',
    label: 'Tätigkeit & Setting',
    shortLabel: 'Setting',
    icon: '🎯',
    weight: 7,
    candidateIntro: 'Was passt besser zu Ihnen? Hier gibt es kein richtig oder falsch.',
    employerIntro: 'Wie ist der Arbeitsalltag in Ihrer Einrichtung geprägt?',
  },
  {
    id: 'entwicklung',
    label: 'Entwicklung & Perspektive',
    shortLabel: 'Entwicklung',
    icon: '📈',
    weight: 4,
    candidateIntro: 'Wie wichtig sind Ihnen Fortbildung und Weiterentwicklung?',
    employerIntro: 'Welche Entwicklungsmöglichkeiten bietet Ihre Einrichtung?',
  },
]

export const CATEGORY_BY_ID: Record<CategoryId, MatchingCategory> = Object.fromEntries(
  CATEGORIES.map((c) => [c.id, c]),
) as Record<CategoryId, MatchingCategory>

/* ---- Slider ---- */

export type SliderType = 'need_fit' | 'preference'

export interface SliderQuestion {
  id: string
  category: CategoryId
  type: SliderType
  /** Frage im Pflegekraft-Formular. */
  labelCandidate: string
  /** Frage im Arbeitgeber-Formular. */
  labelEmployer: string
  candidateMinLabel: string
  candidateMaxLabel: string
  employerMinLabel: string
  employerMaxLabel: string
  /** Slider geht für Retention-Fit & Risikoabzüge ein. */
  riskRelevant: boolean
  /**
   * Nur Pflegekraft beantwortet (z. B. „wertschätzende Führung"). Wird im
   * Match-Score NICHT gewichtet, dient nur dem Matching-Profil der PK.
   */
  pkOnly?: boolean
  /**
   * Arbeitgeber beantwortet diesen Slider NICHT direkt — der AG-Wert wird
   * stattdessen aus den konkreten Faktenfeldern abgeleitet (runner.toJob).
   */
  derived?: boolean
}

/** Need-Fit-Slider: Wichtigkeit (Pflegekraft) trifft Angebot (Arbeitgeber). */
function nf(
  id: string,
  category: CategoryId,
  labelCandidate: string,
  labelEmployer: string,
  employerMinLabel: string,
  employerMaxLabel: string,
  riskRelevant = false,
): SliderQuestion {
  return {
    id,
    category,
    type: 'need_fit',
    labelCandidate,
    labelEmployer,
    candidateMinLabel: 'unwichtig',
    candidateMaxLabel: 'extrem wichtig',
    employerMinLabel,
    employerMaxLabel,
    riskRelevant,
  }
}

/** Präferenz-Slider: echte Gegensätze, Nähe zählt. */
function pref(
  id: string,
  category: CategoryId,
  labelCandidate: string,
  labelEmployer: string,
  minLabel: string,
  maxLabel: string,
): SliderQuestion {
  return {
    id,
    category,
    type: 'preference',
    labelCandidate,
    labelEmployer,
    candidateMinLabel: minLabel,
    candidateMaxLabel: maxLabel,
    employerMinLabel: minLabel,
    employerMaxLabel: maxLabel,
    riskRelevant: false,
  }
}

export const SLIDERS: SliderQuestion[] = [
  // ---- A. Dienstplan & Vereinbarkeit ----
  nf('schedule_reliability', 'dienstplan',
    'Wie wichtig ist Ihnen ein verbindlicher Dienstplan?',
    'Wie verbindlich sind Dienstpläne in Ihrer Einrichtung?',
    'häufige Änderungen', 'sehr verbindlich', true),
  nf('schedule_notice', 'dienstplan',
    'Wie wichtig ist Ihnen eine langfristige Dienstplanung?',
    'Wie früh stehen Dienstpläne in der Regel fest?',
    'sehr kurzfristig', 'langfristig', true),
  nf('no_free_time_callin', 'dienstplan',
    'Wie wichtig ist Ihnen, nicht aus der Freizeit einspringen zu müssen?',
    'Wie selten ist Einspringen aus der Freizeit?',
    'häufig', 'sehr selten', true),
  nf('overtime_control', 'dienstplan',
    'Wie wichtig ist Ihnen, dass Überstunden selten und planbar sind?',
    'Wie gut sind Überstunden planbar und begrenzt?',
    'häufig / unplanbar', 'selten / geregelt', true),
  nf('weekend_predictability', 'dienstplan',
    'Wie wichtig ist Ihnen eine faire, planbare Wochenendregelung?',
    'Wie fair und planbar sind Wochenenddienste?',
    'wenig planbar', 'sehr planbar', false),
  nf('family_compatibility', 'dienstplan',
    'Wie wichtig ist Ihnen Vereinbarkeit mit Familie und Privatleben?',
    'Wie familienfreundlich ist das Arbeitszeitmodell?',
    'kaum', 'sehr stark', true),

  // ---- B. Belastung & Personalbesetzung ----
  nf('staffing_level', 'belastung',
    'Wie wichtig ist Ihnen eine gute Personalbesetzung?',
    'Wie gut ist Ihre Einrichtung personell besetzt?',
    'kritisch', 'sehr stabil', true),
  nf('workload_level', 'belastung',
    'Wie wichtig ist Ihnen ein realistisches Arbeitspensum?',
    'Wie realistisch ist das Arbeitspensum?',
    'sehr hoch', 'gut machbar', true),
  nf('break_reliability', 'belastung',
    'Wie wichtig ist Ihnen, Pausen tatsächlich nehmen zu können?',
    'Wie zuverlässig können Pausen genommen werden?',
    'selten', 'sehr zuverlässig', false),
  nf('physical_relief', 'belastung',
    'Wie wichtig sind Ihnen körperliche Entlastung und Hilfsmittel?',
    'Wie gut sind Hilfsmittel und körperliche Entlastung vorhanden?',
    'kaum', 'sehr gut', true),
  nf('documentation_relief', 'belastung',
    'Wie wichtig ist Ihnen Entlastung bei Dokumentation und Bürokratie?',
    'Wie stark wird die Dokumentation erleichtert?',
    'sehr belastend', 'gut unterstützt', true),
  nf('support_staff', 'belastung',
    'Wie wichtig ist Ihnen Unterstützung durch Assistenz- und Hilfskräfte?',
    'Wie gut ist die Unterstützung durch Hilfs-/Assistenzkräfte?',
    'kaum', 'sehr gut', false),

  // ---- C. Führung, Team & Anerkennung ----
  nf('appreciative_leadership', 'fuehrung',
    'Wie wichtig ist Ihnen wertschätzende Führung?',
    'Wie wertschätzend ist die direkte Führungskultur?',
    'problematisch', 'sehr wertschätzend', true),
  nf('team_fairness', 'fuehrung',
    'Wie wichtig ist Ihnen ein fairer Umgang im Team?',
    'Wie fair ist der Umgang im Team?',
    'angespannt', 'sehr fair', true),
  nf('feedback_culture', 'fuehrung',
    'Wie wichtig ist Ihnen regelmäßiges Feedback?',
    'Wie regelmäßig gibt es Feedbackgespräche?',
    'selten', 'regelmäßig', false),
  nf('recognition', 'fuehrung',
    'Wie wichtig ist Ihnen echte Anerkennung Ihrer Arbeit?',
    'Wie sichtbar wird gute Arbeit anerkannt?',
    'kaum', 'sehr stark', true),

  // ---- D. Pflegequalität & Handlungsspielraum ----
  nf('time_for_care', 'pflegequalitaet',
    'Wie wichtig ist Ihnen ausreichend Zeit für gute Pflege?',
    'Wie viel Zeit bleibt realistisch für gute Pflege?',
    'kaum', 'ausreichend', true),
  nf('professional_autonomy', 'pflegequalitaet',
    'Wie wichtig ist Ihnen fachlicher Handlungsspielraum?',
    'Wie viel fachlicher Handlungsspielraum besteht?',
    'wenig', 'viel', true),
  nf('patient_relationship', 'pflegequalitaet',
    'Wie wichtig ist Ihnen Zeit für Beziehung und Zuwendung?',
    'Wie viel Raum gibt es für Beziehung und Zuwendung?',
    'wenig', 'viel', false),
  // ---- F. Tätigkeit & Setting (Präferenz-Slider) ----
  pref('routine_vs_variety', 'setting',
    'Was passt besser zu Ihnen?',
    'Wie ist der Arbeitsalltag in Ihrer Einrichtung?',
    'klare Routine', 'viel Abwechslung'),
  pref('independent_vs_team', 'setting',
    'Was passt besser zu Ihnen?',
    'Wie wird in Ihrer Einrichtung gearbeitet?',
    'sehr selbstständig', 'eng im Team'),
  pref('relationship_vs_acute', 'setting',
    'Was passt besser zu Ihnen?',
    'Wo liegt der Schwerpunkt der Pflege?',
    'Beziehungspflege', 'akute, technische Pflege'),

  // ---- G. Entwicklung & Perspektive ----
  nf('training_importance', 'entwicklung',
    'Wie wichtig sind Ihnen Fortbildungen?',
    'Wie stark werden Fortbildungen angeboten?',
    'kaum', 'sehr stark', false),
  nf('career_progression', 'entwicklung',
    'Wie wichtig sind Ihnen Aufstiegsmöglichkeiten?',
    'Wie gut sind Aufstiegsmöglichkeiten?',
    'kaum', 'sehr gut', true),
]

// ---- pkOnly / derived Flags nachträglich markieren ----

/** Slider, die NUR von der Pflegekraft beantwortet werden (kein Match-Score). */
const PK_ONLY_SLIDERS = new Set<string>([
  'appreciative_leadership',
  'team_fairness',
])

/** Slider, deren AG-Wert aus den konkreten Faktenfeldern abgeleitet wird. */
const DERIVED_SLIDERS = new Set<string>([
  // Kategorie A — komplett aus Dienstplan-Realität ableitbar
  'schedule_reliability',
  'schedule_notice',
  'no_free_time_callin',
  'overtime_control',
  'weekend_predictability',
  'family_compatibility',
  // Kategorie B — teilweise aus Fakten ableitbar
  'workload_level',
  'physical_relief',
  'documentation_relief',
  'support_staff',
  // Kategorie C
  'feedback_culture',
])

for (const s of SLIDERS) {
  if (PK_ONLY_SLIDERS.has(s.id)) s.pkOnly = true
  if (DERIVED_SLIDERS.has(s.id)) s.derived = true
}

export const SLIDER_BY_ID: Record<string, SliderQuestion> = Object.fromEntries(
  SLIDERS.map((s) => [s.id, s]),
)

export function slidersForCategory(category: CategoryId): SliderQuestion[] {
  return SLIDERS.filter((s) => s.category === category)
}

/** Slider, die im Pflegekraft-Funnel angezeigt werden (alle der Kategorie). */
export function pkSlidersForCategory(category: CategoryId): SliderQuestion[] {
  return SLIDERS.filter((s) => s.category === category)
}

/** Slider, die der Arbeitgeber im Funnel direkt rated (ohne pkOnly + ohne derived). */
export function agSlidersForCategory(category: CategoryId): SliderQuestion[] {
  return SLIDERS.filter((s) => s.category === category && !s.pkOnly && !s.derived)
}

/* ---- Wechselgründe (Retention-Fit) ---- */

export interface SwitchReason {
  id: string
  label: string
  icon: string
  /**
   * Arbeitgeber-Dimension, an der geprüft wird, ob der Wechselgrund
   * an der neuen Einrichtung gelöst wäre. Slider-ID, 'salary_fit' oder
   * null (kein direkt prüfbares Gegenstück).
   */
  mappedDimension: string | null
}

export const SWITCH_REASONS: SwitchReason[] = [
  { id: 'hohe_arbeitsbelastung', label: 'Zu hohe Arbeitsbelastung', icon: '😰', mappedDimension: 'workload_level' },
  { id: 'unterbesetzung', label: 'Zu wenig Personal / Unterbesetzung', icon: '🧍', mappedDimension: 'staffing_level' },
  { id: 'viele_ueberstunden', label: 'Zu viele Überstunden', icon: '⏱️', mappedDimension: 'overtime_control' },
  { id: 'einspringen_freizeit', label: 'Häufiges Einspringen aus der Freizeit', icon: '📞', mappedDimension: 'no_free_time_callin' },
  { id: 'unzuverlaessige_dienstplaene', label: 'Unzuverlässige Dienstpläne', icon: '📅', mappedDimension: 'schedule_reliability' },
  { id: 'schlechte_fuehrung', label: 'Schlechte Führung', icon: '👔', mappedDimension: 'appreciative_leadership' },
  { id: 'schlechtes_teamklima', label: 'Schlechtes Teamklima', icon: '🌧️', mappedDimension: 'team_fairness' },
  { id: 'wenig_zeit_pflege', label: 'Zu wenig Zeit für gute Pflege', icon: '⏳', mappedDimension: 'time_for_care' },
  { id: 'wenig_handlungsspielraum', label: 'Zu wenig Handlungsspielraum', icon: '🔒', mappedDimension: 'professional_autonomy' },
  { id: 'zu_viel_buerokratie', label: 'Zu viel Dokumentation / Bürokratie', icon: '📑', mappedDimension: 'documentation_relief' },
  { id: 'schlechte_vereinbarkeit', label: 'Schlechte Vereinbarkeit mit Privatleben', icon: '👨‍👩‍👧', mappedDimension: 'family_compatibility' },
  { id: 'gehalt_unpassend', label: 'Gehalt nicht passend', icon: '💰', mappedDimension: 'salary_fit' },
  { id: 'keine_entwicklung', label: 'Keine Entwicklungsmöglichkeiten', icon: '📈', mappedDimension: 'career_progression' },
  { id: 'koerperliche_belastung', label: 'Körperliche Belastung zu hoch', icon: '💪', mappedDimension: 'physical_relief' },
  { id: 'integration_probleme', label: 'Probleme bei Anerkennung / Integration', icon: '🌍', mappedDimension: 'onboarding_importance' },
]

export const SWITCH_REASON_BY_ID: Record<string, SwitchReason> = Object.fromEntries(
  SWITCH_REASONS.map((r) => [r.id, r]),
)

/** Gewichtungsfaktor je Top-Rang eines Wechselgrunds. */
export const TOP_REASON_FACTOR: Record<number, number> = { 1: 1.5, 2: 1.3, 3: 1.15 }

/* ---- No-Gos ---- */

export interface NoGo {
  id: string
  label: string
  icon: string
}

export const NO_GOS: NoGo[] = [
  { id: 'keine_nachtdienste', label: 'Keine Nachtdienste', icon: '🌙' },
  { id: 'keine_geteilten_dienste', label: 'Keine geteilten Dienste', icon: '✂️' },
  { id: 'keine_rufbereitschaft', label: 'Keine Rufbereitschaft', icon: '📟' },
  { id: 'keine_wochenenddienste', label: 'Keine regelmäßigen Wochenenddienste', icon: '🗓️' },
  { id: 'keine_kurzfristigen_aenderungen', label: 'Keine kurzfristigen Dienstplanänderungen', icon: '⚡' },
]

export const NO_GO_BY_ID: Record<string, NoGo> = Object.fromEntries(
  NO_GOS.map((n) => [n.id, n]),
)
